import { api } from "./api";
import {
  ListRequest,
  ListRequestSchemaV2,
  MessageResponse,
  PaginatedResponse,
} from "../types/common/commonTypes";
import {
  PurchOrderCreate,
  PurchOrderFull,
  PurchOrderListView,
  PaginatedPOResponse,
  PurchOrderStatusUpdate,
  PurchOrderUpdate,
  POReceiving,
  POReceiptResponse,
  ReceivingDocuments,
} from "../types/purchOrderTypes";
import {
  OrderStatusUpdate,
  CompanyOrderListView,
} from "../types/combinedTypes";

/**
 * Inventory Management Service
 *
 * Provides complete integration with all 12 inventory management API endpoints:
 * - Core lot management (CRUD operations)
 * - FIFO allocation engine
 * - Real-time availability tracking
 * - Lot shipping and movement
 * - Comprehensive inventory summaries
 *
 * Auto-generation: Lot numbers are auto-generated by backend (YY-###### format)
 * Example: 24-000001, 24-000002, etc. (resets annually)
 */
const po_base = "/orders/purchase";
export const purchOrderService = {
  // ========================================
  // Core Inventory Lot Management (6 endpoints)
  // ========================================

  /**
   * Create a new inventory lot
   * POST /api/v1/inventory/lots
   * Note: Lot numbers are auto-generated by backend (format: YY-######)
   */
  create: async (data: PurchOrderCreate): Promise<PurchOrderFull> => {
    const response = await api.post(`${po_base}/`, data);
    return response.data;
  },
  list: async (params: ListRequest): Promise<PaginatedPOResponse> => {
    const response = await api.post(`${po_base}/search`, params);
    return response.data;
  },
  listByVendor: async (
    params: ListRequestSchemaV2,
  ): Promise<PaginatedResponse<CompanyOrderListView>> => {
    const response = await api.post(`${po_base}/by_vendor`, params);
    return response.data;
  },

  /**
   * Get a specific inventory lot by ID
   * GET /api/v1/inventory/lots/{lot_id}
   */
  getById: async (id: string): Promise<PurchOrderFull> => {
    const response = await api.get(`${po_base}/${id}`);
    return response.data;
  },
  postProcess: async (id: string): Promise<MessageResponse> => {
    const response = await api.put(`${po_base}/postprocess/${id}`);
    return response.data;
  },
  regenDoc: async (id: string): Promise<MessageResponse> => {
    const response = await api.put(`${po_base}/regendocs/${id}`);
    return response.data;
  },

  /**
   * Update an inventory lot
   * PUT /api/v1/inventory/lots/{lot_id}
   */
  update: async (
    id: string,
    data: PurchOrderUpdate,
  ): Promise<PurchOrderFull> => {
    const response = await api.put(`${po_base}/${id}`, { ...data, id });
    return response.data;
  },
  updateApproval: async (
    id: string,
    data: PurchOrderStatusUpdate,
  ): Promise<PurchOrderFull> => {
    const response = await api.put(`${po_base}/${id}/approve`, { ...data, id });
    return response.data;
  },
  markComplete: async (id: string): Promise<PurchOrderFull> => {
    const response = await api.put(`${po_base}/${id}/complete`, { id });
    return response.data;
  },
  markVoid: async (id: string): Promise<PurchOrderFull> => {
    const response = await api.put(`${po_base}/${id}/void`, { id });
    return response.data;
  },
  statusUpdate: async (data: OrderStatusUpdate): Promise<MessageResponse> => {
    const response = await api.post(`${po_base}/status-update`, data);
    return response.data;
  },
  /**
   * Update an inventory lot
   * PUT /api/v1/inventory/lots/{lot_id}
   */
  receive: async (
    id: string,
    data: POReceiving,
  ): Promise<POReceiptResponse> => {
    const response = await api.post(`${po_base}/${id}/receive`, {
      ...data,
      id,
    });
    return response.data;
  },
  generateRecDocs: async (
    data: POReceiptResponse,
  ): Promise<ReceivingDocuments> => {
    const response = await api.post(`${po_base}/generate_recdocs`, data);
    return response.data;
  },

  /**
   * Delete an inventory lot (soft delete)
   * DELETE /api/v1/inventory/lots/{lot_id}
   */
  deactivate: async (id: string): Promise<MessageResponse> => {
    const response = await api.delete(`${po_base}/${id}`);
    return response.data;
  },

  /**
   * Restore a deleted inventory lot
   * POST /api/v1/inventory/lots/{lot_id}/restore
   */
  reactivate: async (id: string): Promise<PurchOrderFull> => {
    const response = await api.post(`${po_base}/${id}/restore`);
    return response.data;
  },
};
